import { existsSync } from "node:fs";
import { join, resolve } from "node:path";
//#region src/paths.ts
/**
* # Where the Neon CLIs keep their files on disk
*
**Deliberately impure.** It reads environment variables and touches the filesystem, which
* `@neon/config` — the package this used to be a subpath of — must never do from its root
* export. It lives here instead of there precisely so that a policy-facing package does not
* carry implementor-only code.
*
* It exists because three separate readers each grew their own answer to "where is the
* config directory", and all three disagreed: `packages/cli` honoured `XDG_CONFIG_HOME` but
* not `NEONCTL_CONFIG_DIR`, `packages/env` honoured the env var but not XDG, and the init
* flow hardcoded `~/.config/neonctl`. With `XDG_CONFIG_HOME` set, the CLI wrote
* credentials somewhere the other two never looked.
*
* ## The directory
*
* `neon` is the current name; `neonctl` is the legacy one, kept readable forever. Resolution,
* each entry winning over the next:
*
* 1. An explicit directory (a `--config-dir` flag) — **exact**, no legacy fallback.
* 2. `NEON_CONFIG_DIR` — exact.
* 3. `NEONCTL_CONFIG_DIR` (legacy name) — exact.
* 4. `$XDG_CONFIG_HOME/neon`, else `<home>/.config/neon`.
*
* An explicitly chosen directory is never paired with a fallback: `--config-dir /tmp/ci` that
* quietly read `~/.config/neonctl` would defeat the point of passing it.
*
* ## The files
*
* {@link resolveConfigFile} answers "which path should I use for this file", and it is the
* same answer for reading and writing:
*
* - Present in `neon/` → use it.
* - Present only in `neonctl/` → **use it there, in place.** An existing credentials file is
*   never copied or moved, so nothing is left behind to go stale and no other tool starts
*   reading an abandoned token.
* - Present in neither → the new location. New files only ever appear under `neon/`.
*/
/** Current directory name. New files are created here. */
const CONFIG_DIR_NAME = "neon";
/** Legacy directory name, read forever so existing installs keep working untouched. */
const LEGACY_CONFIG_DIR_NAME = "neonctl";
/** Where files are created. See the module docs for the precedence. */
function configDir(options = {}) {
	const explicit = explicitDir(options);
	if (explicit) return explicit;
	return join(configHome(options.env ?? process.env), CONFIG_DIR_NAME);
}
/**
* The legacy directory, or `undefined` when the location was chosen explicitly (in which
* case there is no legacy counterpart to fall back to).
*/
function legacyConfigDir(options = {}) {
	if (explicitDir(options)) return void 0;
	return join(configHome(options.env ?? process.env), LEGACY_CONFIG_DIR_NAME);
}
/**
* Resolve one file inside the config directory. Prefers the current location, falls back to
* an existing legacy file **in place**, and otherwise points at the current location so new
* files are created there.
*/
function resolveConfigFile(fileName, options = {}) {
	const dir = configDir(options);
	const current = resolve(dir, fileName);
	if (existsSync(current)) return {
		path: current,
		dir,
		isLegacy: false,
		exists: true
	};
	const legacyDir = legacyConfigDir(options);
	if (legacyDir) {
		const legacy = resolve(legacyDir, fileName);
		if (existsSync(legacy)) return {
			path: legacy,
			dir: legacyDir,
			isLegacy: true,
			exists: true
		};
	}
	return {
		path: current,
		dir,
		isLegacy: false,
		exists: false
	};
}
/** `$XDG_CONFIG_HOME`, else `<home>/.config`. Falls back to a relative `.config` with no home. */
function configHome(env) {
	const xdg = nonEmpty(env.XDG_CONFIG_HOME);
	if (xdg) return xdg;
	const home = nonEmpty(env.HOME) ?? nonEmpty(env.USERPROFILE);
	return home ? join(home, ".config") : ".config";
}
function explicitDir(options) {
	const env = options.env ?? process.env;
	return nonEmpty(options.dir) ?? nonEmpty(env.NEON_CONFIG_DIR) ?? nonEmpty(env.NEONCTL_CONFIG_DIR);
}
function nonEmpty(value) {
	if (typeof value !== "string") return void 0;
	const trimmed = value.trim();
	return trimmed === "" ? void 0 : trimmed;
}
const CREDENTIALS_FILE = "credentials.json";
/**
* Default for `--config-dir`: `$XDG_CONFIG_HOME/neon`, else `~/.config/neon`.
*
* The directory was called `neonctl` until the CLI was renamed. An existing one is still read —
* see {@link credentialsPath} — but it is never written to, moved, or deleted.
*/
const defaultDir = configDir();
/**
* Where this invocation's `credentials.json` lives.
*
* When `--config-dir` was left at its default, an existing file in the legacy `neonctl`
* directory is used **in place**: an install that predates the rename keeps working, and its
* credentials are never duplicated into a second location where one copy could go stale while
* another tool still reads it.
*
* A `--config-dir` the user actually passed is used exactly as given. Falling back out of an
* explicitly chosen directory would defeat the reason for choosing it — a CI run pointed at a
* scratch directory must never pick up a developer's real credentials.
*/
const credentialsPath = (dir) => resolveConfigFile(CREDENTIALS_FILE, dir === defaultDir ? {} : { dir }).path;
/**
* Whether a credentials file is one the CLI created, rather than a path a profile adopted.
*
* Anything that deletes a credential has to ask this first. A profile entry may point anywhere —
* that is what makes adopting an existing directory a one-line edit — and a file we did not
* create is not ours to remove.
*/
const isInsideConfigDir = (configDirectory, file) => `${resolve(file)}/`.startsWith(`${resolve(configDirectory)}/`);
/**
* Whether a credentials file is one the CLI owns, counting the legacy `neonctl` directory.
*
* {@link credentialsPath} deliberately reads an existing legacy file in place rather than
* migrating it, so for a default config directory that file is ours even though it sits outside
* `neon/`. Judging ownership on the current directory alone would call an install that predates
* the rename "adopted".
*/
const isOwnedCredentialPath = (configDirectory, file) => {
	if (isInsideConfigDir(configDirectory, file)) return true;
	if (configDirectory !== defaultDir) return false;
	const legacy = legacyConfigDir();
	return legacy !== void 0 && isInsideConfigDir(legacy, file);
};
//#endregion
export { CONFIG_DIR_NAME, CREDENTIALS_FILE, LEGACY_CONFIG_DIR_NAME, configDir, credentialsPath, defaultDir, isInsideConfigDir, isOwnedCredentialPath, legacyConfigDir, resolveConfigFile };

//# sourceMappingURL=paths.js.map